import React, { useEffect, useRef } from "react";

/**
 * NeuralCanvas (theme-aware)
 * Reads --accent-cyan/blue/violet from :root and redraws when the theme changes.
 */
const NeuralCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch = window.matchMedia("(pointer: coarse)").matches;

    let width = 0, height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    let nodes = [];
    let edges = [];
    let signals = [];
    const mouse = { x: -9999, y: -9999, active: false };
    let scrollY = window.scrollY || 0;
    let lastScrollY = scrollY;
    let scrollForce = 0;
    let rafId = 0;
    let lastTs = 0;
    let running = true;
    let currentTheme = "dark";

    const parseRgb = (name) => {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      if (!v) return [0, 240, 255];
      const parts = v.split(/\s+|,/).map((n) => parseInt(n, 10));
      return [parts[0] || 0, parts[1] || 240, parts[2] || 255];
    };

    let PALETTES = [];
    const refreshPalette = () => {
      const cyan = parseRgb("--accent-cyan-rgb");
      const blue = parseRgb("--accent-blue-rgb");
      const violet = parseRgb("--accent-violet-rgb");
      PALETTES = [
        { core: cyan, label: "memory" },
        { core: blue, label: "voice" },
        { core: violet, label: "goal" },
      ];
      currentTheme = document.documentElement.getAttribute("data-theme")
        || (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");
    };
    refreshPalette();

    const rand = (min, max) => Math.random() * (max - min) + min;

    const config = () => {
      const isMobile = window.innerWidth < 768;
      const isSmall = window.innerWidth < 1200;
      return {
        count: prefersReduced ? 34 : isMobile ? 52 : isSmall ? 82 : 118,
        linkDist: isMobile ? 120 : 165,
        maxEdgesPerNode: 4,
        mouseRadius: isMobile ? 120 : 200,
        ambientFireRate: prefersReduced ? 0 : 0.012,
      };
    };

    let cfg = config();

    const build = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cfg = config();
      nodes = [];
      edges = [];
      signals = [];
      for (let i = 0; i < cfg.count; i++) {
        const p = PALETTES[i % PALETTES.length];
        nodes.push({
          x: rand(0, width), y: rand(0, height),
          baseX: 0, baseY: 0,
          vx: rand(-0.05, 0.05), vy: rand(-0.05, 0.05),
          r: rand(1.1, 2.4),
          glow: rand(0.15, 0.35),
          pulse: Math.random() * Math.PI * 2,
          palette: p,
          charge: 0,
        });
      }
      nodes.forEach((n) => { n.baseX = n.x; n.baseY = n.y; });
      const maxEdges = cfg.maxEdgesPerNode;
      const linkDist = cfg.linkDist;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const candidates = [];
        for (let j = 0; j < nodes.length; j++) {
          if (i === j) continue;
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y, d2 = dx * dx + dy * dy;
          if (d2 < linkDist * linkDist) candidates.push({ j, d2 });
        }
        candidates.sort((x, y) => x.d2 - y.d2);
        const take = candidates.slice(0, maxEdges);
        for (const c of take) {
          const key = i < c.j ? `${i}-${c.j}` : `${c.j}-${i}`;
          if (!edges.find((e) => e.key === key)) edges.push({ key, a: i, b: c.j, d2: c.d2 });
        }
      }
    };

    const firePulseAt = (x, y, intensity = 1) => {
      let closest = -1, best = Infinity;
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const dx = n.x - x, dy = n.y - y, d2 = dx * dx + dy * dy;
        if (d2 < best) { best = d2; closest = i; }
      }
      if (closest < 0) return;
      nodes[closest].charge = Math.min(1.5, nodes[closest].charge + 0.9 * intensity);
      const outgoing = edges.filter((e) => e.a === closest || e.b === closest).slice(0, 5);
      for (const e of outgoing) {
        signals.push({
          edge: e, from: closest, to: e.a === closest ? e.b : e.a,
          t: 0, speed: rand(0.012, 0.022), strength: 0.9 * intensity,
        });
      }
    };

    const ambientFire = () => {
      if (Math.random() < cfg.ambientFireRate) {
        const n = nodes[Math.floor(Math.random() * nodes.length)];
        if (n) firePulseAt(n.x, n.y, 0.6);
      }
    };

    const step = (ts) => {
      if (!running) return;
      const dt = Math.min(40, ts - lastTs || 16);
      lastTs = ts;

      ctx.clearRect(0, 0, width, height);

      const isLight = currentTheme === "light";

      // Smooth scroll fluid breeze physics
      let scrollDelta = scrollY - lastScrollY;
      lastScrollY = scrollY;
      const maxDelta = 100;
      if (scrollDelta > maxDelta) scrollDelta = maxDelta;
      if (scrollDelta < -maxDelta) scrollDelta = -maxDelta;
      scrollForce += (scrollDelta - scrollForce) * 0.08;

      // Very subtle backdrop bloom
      ctx.save();
      const grad = ctx.createRadialGradient(width * 0.5, height * 0.35, 0, width * 0.5, height * 0.35, Math.max(width, height) * 0.7);
      if (isLight) {
        grad.addColorStop(0, `rgba(${PALETTES[0].core.join(",")},0.05)`);
        grad.addColorStop(1, `rgba(246,245,240,0)`);
      } else {
        grad.addColorStop(0, "rgba(12, 22, 40, 0.25)");
        grad.addColorStop(1, "rgba(5, 5, 10, 0)");
      }
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();

      // Move nodes
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.pulse += 0.012;
        n.vx += rand(-0.002, 0.002);
        n.vy += rand(-0.002, 0.002);
        n.vx *= 0.985;
        n.vy *= 0.985;

        // Fluid inertia shift: scroll acts as a virtual wind
        n.vy -= scrollForce * 0.0007;

        n.x += n.vx;
        n.y += n.vy + Math.sin(n.pulse * 0.5) * 0.04;
        n.x += (n.baseX - n.x) * 0.003;
        n.y += (n.baseY - n.y) * 0.003;
        const dx = n.x - mouse.x, dy = n.y - mouse.y, d = Math.hypot(dx, dy);
        if (mouse.active && d < cfg.mouseRadius) {
          const f = 1 - d / cfg.mouseRadius;
          n.charge = Math.min(1.5, n.charge + f * 0.07);
          n.vx += (dx / (d + 0.001)) * f * 0.02;
          n.vy += (dy / (d + 0.001)) * f * 0.02;
        }
        n.charge *= 0.96;
      }

      // Edges — stronger in light mode so they remain visible
      const edgeAlphaBase = isLight ? 0.11 : 0.06;
      const edgeAlphaGain = isLight ? 0.28 : 0.16;

      for (let k = 0; k < edges.length; k++) {
        const e = edges[k];
        const a = nodes[e.a], b = nodes[e.b];
        const dx = a.x - b.x, dy = a.y - b.y, dist = Math.hypot(dx, dy);
        if (dist > cfg.linkDist * 1.15) continue;
        const baseAlpha = edgeAlphaBase + (1 - dist / cfg.linkDist) * edgeAlphaGain;
        const charge = (a.charge + b.charge) * 0.5;
        const mouseBoost = mouse.active
          ? Math.max(0, 1 - Math.min(
              Math.hypot(mouse.x - a.x, mouse.y - a.y),
              Math.hypot(mouse.x - b.x, mouse.y - b.y)
            ) / cfg.mouseRadius) * 0.4
          : 0;
        const alpha = Math.min(1, baseAlpha + charge * 0.4 + mouseBoost);
        const col = a.palette.core;
        ctx.strokeStyle = `rgba(${col[0]}, ${col[1]}, ${col[2]}, ${alpha})`;
        ctx.lineWidth = 0.7 + charge * 0.7 + mouseBoost * 0.5;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      // Signals
      for (let s = signals.length - 1; s >= 0; s--) {
        const sig = signals[s];
        sig.t += sig.speed * (dt / 16.6);
        if (sig.t >= 1) {
          const arrived = nodes[sig.to];
          arrived.charge = Math.min(1.5, arrived.charge + sig.strength * 0.5);
          const onward = edges.filter((e) => (e.a === sig.to || e.b === sig.to) && !(e.a === sig.from || e.b === sig.from));
          if (sig.strength > 0.3 && onward.length) {
            const pick = onward[Math.floor(Math.random() * onward.length)];
            signals.push({
              edge: pick, from: sig.to, to: pick.a === sig.to ? pick.b : pick.a,
              t: 0, speed: sig.speed * 0.92, strength: sig.strength * 0.55,
            });
          }
          signals.splice(s, 1);
          continue;
        }
        const a = nodes[sig.from], b = nodes[sig.to];
        const x = a.x + (b.x - a.x) * sig.t;
        const y = a.y + (b.y - a.y) * sig.t;
        const col = a.palette.core;
        ctx.save();
        ctx.shadowBlur = isLight ? 10 : 16;
        ctx.shadowColor = `rgba(${col[0]}, ${col[1]}, ${col[2]}, 0.85)`;
        ctx.fillStyle = `rgba(${col[0]}, ${col[1]}, ${col[2]}, ${0.85 * sig.strength})`;
        ctx.beginPath();
        ctx.arc(x, y, 1.8 + sig.strength * 0.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Nodes
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const mouseDist = mouse.active ? Math.hypot(mouse.x - n.x, mouse.y - n.y) : Infinity;
        const proximity = mouse.active ? Math.max(0, 1 - mouseDist / cfg.mouseRadius) : 0;
        const energy = Math.min(1.5, n.glow + n.charge + proximity * 0.9 + Math.sin(n.pulse) * 0.08);
        const col = n.palette.core;
        const haloR = (isLight ? 12 : 18) + energy * (isLight ? 14 : 22);
        const halo = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, haloR);
        halo.addColorStop(0, `rgba(${col[0]}, ${col[1]}, ${col[2]}, ${(isLight ? 0.22 : 0.32) * Math.min(1.1, energy)})`);
        halo.addColorStop(1, `rgba(${col[0]}, ${col[1]}, ${col[2]}, 0)`);
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(n.x, n.y, haloR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(${col[0]}, ${col[1]}, ${col[2]}, 0.92)`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + energy * 0.8, 0, Math.PI * 2);
        ctx.fill();

        if (!isLight) {
          ctx.fillStyle = `rgba(255,255,255, ${0.3 + energy * 0.45})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, 0.7, 0, Math.PI * 2);
          ctx.fill();
        }

        if (Math.random() < 0.00035 && energy > 0.4) firePulseAt(n.x, n.y, 0.7);
      }

      ambientFire();

      rafId = requestAnimationFrame(step);
    };

    const onMove = (e) => {
      mouse.x = e.clientX; mouse.y = e.clientY; mouse.active = true;
      if (Math.random() < 0.04) firePulseAt(e.clientX, e.clientY, 0.4);
    };
    const onLeave = () => { mouse.active = false; mouse.x = -9999; mouse.y = -9999; };
    const onScroll = () => {
      scrollY = window.scrollY || 0;
      if (!isTouch && Math.random() < 0.08) {
        const x = rand(0, width), y = rand(0, height);
        firePulseAt(x, y, 0.5);
      }
    };
    const onResize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      build();
    };
    const onVisibility = () => {
      if (document.hidden) { cancelAnimationFrame(rafId); }
      else { lastTs = performance.now(); rafId = requestAnimationFrame(step); }
    };
    const onTheme = () => { refreshPalette(); };

    window.__auraNeural = {
      pulseAt: (x, y, intensity = 1) => firePulseAt(x, y, intensity),
      pulseElement: (el, intensity = 1) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        firePulseAt(r.left + r.width / 2, r.top + r.height / 2, intensity);
      },
    };

    build();
    rafId = requestAnimationFrame(step);
    if (!isTouch) {
      window.addEventListener("mousemove", onMove, { passive: true });
      window.addEventListener("mouseleave", onLeave);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("aura:theme", onTheme);

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      if (!isTouch) {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseleave", onLeave);
      }
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("aura:theme", onTheme);
      try { delete window.__auraNeural; } catch (e) { /* noop */ }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="neural-canvas"
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", zIndex: 0, pointerEvents: "none" }}
    />
  );
};

export default NeuralCanvas;
